import { useEffect, useRef } from 'react';

/**
 * Animated canvas starfield / drifting particles used behind cinematic sections.
 * Lightweight, respects reduced-motion, cleans up on unmount.
 */
export default function Starfield({ density = 90, className = '' }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let w = 0;
    let h = 0;
    let stars = [];

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      stars = Array.from({ length: density }).map(() => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.4 + 0.2,
        s: Math.random() * 0.25 + 0.05,
        a: Math.random() * 0.7 + 0.3,
        t: Math.random() * Math.PI * 2,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const st of stars) {
        st.t += 0.02;
        st.x += st.s;
        if (st.x > w + 4) st.x = -4;
        const twinkle = 0.6 + Math.sin(st.t) * 0.4;
        ctx.beginPath();
        ctx.arc(st.x, st.y, st.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(186, 230, 253, ${st.a * twinkle})`;
        ctx.fill();
      }
      rafRef.current = requestAnimationFrame(draw);
    };

    const prefersReducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    resize();
    if (!prefersReducedMotion) rafRef.current = requestAnimationFrame(draw);
    else draw();

    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [density]);

  return <canvas ref={canvasRef} className={`w-full h-full ${className}`} />;
}
